import { Injectable, OnDestroy } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    isCategoryHeaderRow,
    isCategoryItemRow,
    isContainedRow,
    isExpandableRow,
    isFullRowContent,
    isGroupHeaderRow,
    isSelectableRow,
    isStandardRow,
} from '../../base-table.helper';
import { GroupWithContentRows, InputRow } from '../../base-table.interface';
import { InputRowSorter, TableInputSorter } from './table-sort.interface';

type GroupedRowSorter<T, G, E> = {
    [groupId: string]: TableInputSorter<T, G, E> | undefined;
};

/**
 * key to use for the "all parts" tab
 */
const ALL_TAB = '__all-parts__';

@Injectable({
    providedIn: null,
})
export class TableSortService<T, G, E> implements OnDestroy {
    public inititizeSorting?: () => void;

    private currentSorter: GroupedRowSorter<T, G, E> = {};
    private currentGroup: GroupWithContentRows<G, T, E> | null = null;

    private activeSorter = new ReplaySubject<TableInputSorter<T, G, E> | undefined>(1);

    /**
     * Emit active sorters whenever one of the sorters or the group changed
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly sorter = this.activeSorter.asObservable();

    /**
     * Get ID for active group
     *
     * @param group Group to get ID for
     * @param allTab ID of "all" tab (used as fallback)
     * @returns ID of current group
     */
    public groupIdentifier: (group: GroupWithContentRows<G, T, E> | null, allTab: string) => string = (
        _,
        allTab
    ) => allTab;

    public ngOnDestroy(): void {
        this.activeSorter.complete();
    }

    public setTextSorter(sortProp: keyof T & string, sortOrder: string) {
        this.setSorter(sortProp, sortOrder, this.getDefaultSorter(sortProp, sortOrder));
    }

    public updateGroupSorter(group?: GroupWithContentRows<G, T, E>) {
        this.currentGroup = group ?? null;
        this.updateActiveSorter();
    }

    public watchPropertyValue(sortProp: keyof T & string): Observable<string> {
        return this.sorter.pipe(
            map((sorter) => (!sorter ? '' : sorter.property === sortProp ? sorter.sortOrder : ''))
        );
    }

    private getDefaultSorter(prop: keyof T, sortOrder: string): InputRowSorter<T, G, E> | null {
        return !sortOrder
            ? null
            : (rowA: InputRow<T, G, E>, rowB: InputRow<T, G, E>) => {
                  if (isGroupHeaderRow(rowA) || isCategoryHeaderRow(rowA)) {
                      return 0;
                  }
                  if (isGroupHeaderRow(rowB) || isCategoryHeaderRow(rowB)) {
                      return 0;
                  }
                  if (
                      isContainedRow(rowA) &&
                      (!isContainedRow(rowB) ||
                          rowA.groupRow.rowData.uniqueGroupId !== rowB.groupRow.rowData.uniqueGroupId)
                  ) {
                      return 0;
                  }

                  const itemValueA =
                      isStandardRow(rowA) ||
                      isExpandableRow(rowA) ||
                      isSelectableRow(rowA) ||
                      isCategoryItemRow(rowA) ||
                      isFullRowContent(rowA) ||
                      isContainedRow(rowA)
                          ? rowA.rowData[prop]
                          : rowA[prop];
                  const itemValueB =
                      isStandardRow(rowB) ||
                      isExpandableRow(rowB) ||
                      isSelectableRow(rowB) ||
                      isCategoryItemRow(rowB) ||
                      isFullRowContent(rowB) ||
                      isContainedRow(rowB)
                          ? rowB.rowData[prop]
                          : rowB[prop];

                  const result =
                      sortOrder === 'ascending'
                          ? String(itemValueA).localeCompare(String(itemValueB), undefined, { numeric: true })
                          : String(itemValueB).localeCompare(String(itemValueA), undefined, {
                                numeric: true,
                            });

                  return result;
              };
    }

    private setSorter(
        sortProp: keyof T & string,
        sortOrder: string,
        sortFn: InputRowSorter<T, G, E> | null
    ): void {
        const groupId = this.getGroupId();

        if (!sortFn) {
            this.currentSorter[groupId] = undefined;
        } else {
            this.currentSorter[groupId] = {
                property: sortProp,
                sortOrder,
                rowSorter: sortFn,
            };
        }

        this.updateActiveSorter();
    }

    private updateActiveSorter() {
        const groupSorter = this.getGroupSorter(this.currentGroup);
        this.activeSorter.next(groupSorter);
    }

    private getGroupSorter(
        group: GroupWithContentRows<G, T, E> | null,
        isRetry = false
    ): TableInputSorter<T, G, E> | undefined {
        const groupId = this.getGroupId(group);
        const groupSorter = this.currentSorter[groupId];

        // set initial sorting if group has no sorter and an initializer function has been provided
        if (groupSorter === undefined && this.inititizeSorting && !isRetry) {
            this.inititizeSorting();

            return this.getGroupSorter(group, true);
        }

        return groupSorter;
    }

    private getGroupId(group = this.currentGroup): string {
        return this.groupIdentifier(group, ALL_TAB);
    }
}
